'use client'

/* eslint-disable @next/next/no-img-element */

import React, { useState, useRef, useEffect, useCallback } from 'react'

import type { HeaderNavItemDropdown, HeaderNavItemDropdownLink } from '@/Header/types'

import Link from 'next/link'
import { cn } from '@/utilities/ui'
import { ChevronRight } from 'lucide-react'

interface DropdownProps {
  item: HeaderNavItemDropdown
}

export const Dropdown: React.FC<DropdownProps> = ({ item }) => {
  const [isOpen, setIsOpen] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null)
  const hoverOpenedRef = useRef(false)
  const openedByClickRef = useRef(false)

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    hoverOpenedRef.current = true
    setIsOpen(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false)
      hoverOpenedRef.current = false
      openedByClickRef.current = false
    }, 150)
  }, [])

  const handleToggle = useCallback(() => {
    if (!isOpen) {
      openedByClickRef.current = true
      setIsOpen(true)
      return
    }

    // Pointer movement opens the menu before the ensuing click fires. Treat
    // that first click as confirmation instead of immediately closing it.
    // Keep ref mutations outside a state updater because React Strict Mode may
    // invoke updater functions twice in development.
    if (!openedByClickRef.current && hoverOpenedRef.current) {
      openedByClickRef.current = true
      return
    }

    openedByClickRef.current = false
    setIsOpen(false)
  }, [isOpen])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      openedByClickRef.current = false
    }
  }, [])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        className={cn(
          'flex items-center gap-1 text-sm font-medium transition-colors duration-200',
          'text-foreground/80 hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2 rounded-sm',
          isOpen && 'text-foreground',
        )}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        {item.dropdownTitle}
        <ChevronRight
          className={cn(
            'h-4 w-4 transition-transform duration-200 rotate-90',
            isOpen && 'rotate-0',
          )}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute top-full left-1/2 -translate-x-1/2 pt-4 z-50',
            'w-screen max-w-3xl',
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onKeyDown={handleKeyDown}
        >
          <div
            className={cn(
              'rounded-xl border border-border bg-background p-6 shadow-xl',
              'animate-in fade-in slide-in-from-top-2 duration-200',
            )}
            role="menu"
            aria-orientation="vertical"
          >
            {item.dropdownDescription && (
              <p className="mb-4 text-sm text-muted-foreground">{item.dropdownDescription}</p>
            )}
            <div className="grid grid-cols-2 gap-4">
              {item.dropdownLinks?.map((link, index) => (
                <DropdownLink key={index} link={link} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const DropdownLink: React.FC<{ link: HeaderNavItemDropdownLink }> = ({ link }) => {
  return (
    <Link
      href={link.url || '#'}
      className={cn(
        'group flex items-start gap-3 rounded-lg p-3',
        'transition-colors duration-200',
        'hover:bg-accent',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2',
      )}
      target={link.newTab ? '_blank' : undefined}
      rel={link.newTab ? 'noopener noreferrer' : undefined}
      role="menuitem"
    >
      {link.icon && typeof link.icon === 'object' && 'url' in link.icon && link.icon.url && (
        <div className="flex-shrink-0">
          <img
            src={link.icon.url}
            alt=""
            width={24}
            height={24}
            className="h-6 w-6 object-contain"
          />
        </div>
      )}
      <div className="flex-1">
        <div
          className={cn(
            'text-sm font-medium transition-colors duration-200',
            'group-hover:text-brand-red',
          )}
        >
          {link.label}
        </div>
        {link.description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{link.description}</p>
        )}
      </div>
      <ChevronRight
        className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        aria-hidden="true"
      />
    </Link>
  )
}
